/**
 * AddEditPopup component is used to add or edit a variation analysis.
 * It handles the form data.
 * Depending on the edit mode, it will display the form with the data to modify or the form to add a new analysis.
 */

import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Form,
  SimpleItem,
  GroupItem,
  RequiredRule,
  Label,
} from "devextreme-react/form";
import PopupWrapper from "../../../../../components/common/pyramid/PopupWrapper";
import {
  HORUS_COUPON_METHOD,
  HORUS_ACTUARIAL_RATE_RULE,
} from "data/ENUM/add-options";
import { useAuth } from "providers/AuthProvider";
import { useGroupedItems } from "hooks/useGroupedItems";
import { useFilterByCurrentGroup } from "hooks/useFilterByCurrentGroup";
import useItemOwner from "hooks/useItemOwner";

export const popupConfigs = {
  add: {
    width: "auto",
    height: "auto",
    title: "New Variation Analysis",
    icon: "add",
  },
  rowEdit: {
    width: "auto",
    height: "auto",
    title: "Edit",
    icon: "edit",
  },
};

/**
 * Props:
 * - isVisible: boolean to display the popup.
 * - onConfirm: function to confirm the changes.
 * - onCancel: function to cancel the changes.
 * - datasetList: list of datasets.
 * - inputFormData: data to modify.
 * - editMode: boolean to modify or add a new analysis.
 */
const AddEditPopup = ({
  isVisible,
  onConfirm,
  onCancel,
  datasetList,
  inputFormData,
  editMode,
}) => {
  // Determine ownership of the item if edit mode is true
  const ownedItem = useItemOwner(editMode ? inputFormData : null, "group");
  const { userList, companyList } = useAuth();

  // Filter datasets on the current group
  const datasetsFilteredByGroup = useFilterByCurrentGroup(datasetList);
  const datasets = editMode ? datasetList : datasetsFilteredByGroup;

  // Group datasets by user
  const groupedDatasets = useGroupedItems(datasets, userList, companyList);

  /**
   * Gets the initial data for the form.
   * If the edit mode is true, returns inputFormData.
   * Otherwise, returns the initial data for the form.
   * @returns {Object} The initial data for the form.
   */
  const getInitialData = () => {
    if (editMode) {
      return {
        ...inputFormData,
        useDepartureActuarialRateRules:
          inputFormData.actuarialRateRulesDeparture != null &&
          inputFormData.actuarialRateRulesDeparture !== 0,
        useArrivalActuarialRateRules:
          inputFormData.actuarialRateRulesArrival != null &&
          inputFormData.actuarialRateRulesArrival !== 0,
      };
    } else {
      return {
        description: "",
        createdAt: null,
        inputDeparture: "",
        inputArrival: "",
        couponMethod: null,
        datasetDepartureId: null,
        datasetArrivalId: null,
        departureDatasetEvaluationDate: null,
        arrivalDatasetEvaluationDate: null,
        actuarialRateRulesDeparture: null,
        actuarialRateRulesArrival: null,
        useDepartureActuarialRateRules: false,
        useArrivalActuarialRateRules: false,
      };
    }
  };

  /* State variables */
  const [localData, setLocalData] = useState(getInitialData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef(null);

  // Save the current config
  const currentConfig = useMemo(
    () => (editMode ? popupConfigs.rowEdit : popupConfigs.add),
    [editMode]
  );

  useEffect(() => {
    setLocalData({
      ...inputFormData,
      useDepartureActuarialRateRules:
        inputFormData.actuarialRateRulesDeparture != null &&
        inputFormData.actuarialRateRulesDeparture !== 0,
      useArrivalActuarialRateRules:
        inputFormData.actuarialRateRulesArrival != null &&
        inputFormData.actuarialRateRulesArrival !== 0,
    });
  }, [inputFormData]);

  useEffect(() => {
    if (localData.datasetDepartureId) {
      const selectedDataset = datasetList.find(
        (dataset) => dataset.id === localData.datasetDepartureId
      );
      setLocalData((prev) => ({
        ...prev,
        departureDatasetEvaluationDate: selectedDataset.evaluationDate,
      }));
    }
  }, [localData.datasetDepartureId, datasetList]);

  useEffect(() => {
    if (localData.datasetArrivalId) {
      const selectedDataset = datasetList.find(
        (dataset) => dataset.id === localData.datasetArrivalId
      );
      setLocalData((prev) => ({
        ...prev,
        arrivalDatasetEvaluationDate: selectedDataset.evaluationDate,
      }));
    }
  }, [localData.datasetArrivalId, datasetList]);


  /**
   * Handles the cancel button click.
   */
  const handleCancel = () => {
    if (isSubmitting) return;

    if (editMode) {
      setLocalData({
        ...inputFormData,
        useDepartureActuarialRateRules:
          inputFormData.actuarialRateRulesDeparture != null &&
          inputFormData.actuarialRateRulesDeparture !== 0,
        useArrivalActuarialRateRules:
          inputFormData.actuarialRateRulesArrival != null &&
          inputFormData.actuarialRateRulesArrival !== 0,
      });
      formRef.current.instance().clear();
    } else {
      setLocalData(getInitialData());
      formRef.current.instance().clear();
    }
    onCancel();
  };

  /**
   * Handles the confirm button click. Checks the form data and confirms the changes.
   */
  const handleConfirm = () => {
    if (isSubmitting) return;

    const formInstance = formRef.current.instance();
    const editableFields = [];

    if (editMode) {
      editableFields.push("description");
    } else {
      editableFields.push(
        "description",
        "couponMethod",
        "datasetDepartureId",
        "datasetArrivalId"
      );
      // Only validate actuarialRateRules if the checkbox is checked
      if (localData.useDepartureActuarialRateRules) {
        editableFields.push("actuarialRateRulesDeparture");
      }
      if (localData.useArrivalActuarialRateRules) {
        editableFields.push("actuarialRateRulesArrival");
      }
    }

    const validationResult = formInstance.validate(editableFields);

    if (validationResult.isValid) {
      confirmAndReset(localData);
    } else {
      console.log("Invalid fields:", validationResult.brokenRules);
    }
  };

  const confirmAndReset = async (data) => {
    setIsSubmitting(true);
    try {
      // If checkbox is not checked, set actuarialRateRules to 'None' -> 0
      const dataToSend = {
        ...data,
        actuarialRateRulesDeparture: data.useDepartureActuarialRateRules
          ? data.actuarialRateRulesDeparture
          : 0, // 0 is the default value for none for backend enums
        actuarialRateRulesArrival: data.useArrivalActuarialRateRules
          ? data.actuarialRateRulesArrival
          : 0,
      };
      await onConfirm(dataToSend);
      formRef.current.instance().resetValues();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFieldChange = (e) => {
    setLocalData({ ...localData, [e.dataField]: e.value });
  };

  const handleDepartureActuarialRateCheckboxChange = (e) => {
    const newFormData = {
      ...localData,
      useDepartureActuarialRateRules: e.value,
    };
    if (!e.value) {
      newFormData.actuarialRateRulesDeparture = null;
    }
    setLocalData(newFormData);
  };

  const handleArrivalActuarialRateCheckboxChange = (e) => {
    const newFormData = {
      ...localData,
      useArrivalActuarialRateRules: e.value,
    };
    if (!e.value) {
      newFormData.actuarialRateRulesArrival = null;
    }
    setLocalData(newFormData);
  };

  return (
    <>
      <PopupWrapper
        {...currentConfig}
        isVisible={isVisible}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        isConfirmDisabled={isSubmitting || (editMode && !ownedItem)}
        isCancelDisabled={isSubmitting}
        showLoading={isSubmitting}
      >
        <Form
          formData={localData}
          ref={formRef}
          onFieldDataChanged={handleFieldChange}
        >
          <GroupItem caption="Informations">
            <SimpleItem
              dataField="description"
              editorType="dxTextBox"
              editorOptions={{
                readOnly: editMode && !ownedItem,
                placeholder: "Enter description",
                onValueChanged: (e) =>
                  setLocalData({ ...localData, description: e.value }),
              }}
              isRequired={true}
            >
              <Label text="Description" />
              <RequiredRule />
            </SimpleItem>
          </GroupItem>

          <GroupItem caption="Data">
            <SimpleItem
              dataField="couponMethod"
              editorType="dxSelectBox"
              editorOptions={{
                items: HORUS_COUPON_METHOD,
                valueExpr: "id",
                displayExpr: "label",
                readOnly: editMode,
                required: !editMode,
              }}
              isRequired={!editMode}
            >
              <Label text="Amortization Coupon method" />
            </SimpleItem>
          </GroupItem>

          <GroupItem caption="Departure" colCount={4}>
            <SimpleItem
              dataField="datasetDepartureId"
              colSpan={2}
              editorType="dxSelectBox"
              editorOptions={{
                dataSource: groupedDatasets,
                displayExpr: "description",
                valueExpr: "id",
                searchEnabled: true,
                grouped: true,
                readOnly: editMode,
                required: !editMode,
              }}
              isRequired={!editMode}
            >
              <Label text="Dataset" />
            </SimpleItem>

            <SimpleItem
              dataField="departureDatasetEvaluationDate"
              name="departureDatasetEvaluationDate"
              colSpan={2}
              editorType="dxDateBox"
              editorOptions={{
                readOnly: true,
                width: "100%",
                displayFormat: "yyyy-MM-dd",
                value: localData.departureDatasetEvaluationDate,
              }}
            >
              <Label text="Evaluation Date" />
            </SimpleItem>

            {!editMode && (
              <>
                <SimpleItem
                  colSpan={2}
                  dataField="useDepartureActuarialRateRules"
                  editorType="dxCheckBox"
                  editorOptions={{
                    readOnly: false,
                    onValueChanged: handleDepartureActuarialRateCheckboxChange,
                  }}
                >
                  <Label text="Use Actuarial Rate" />
                </SimpleItem>

                <SimpleItem
                  colSpan={2}
                  dataField="actuarialRateRulesDeparture"
                  editorType="dxSelectBox"
                  editorOptions={{
                    dataSource: HORUS_ACTUARIAL_RATE_RULE,
                    displayExpr: "label",
                    valueExpr: "id",
                    readOnly: false,
                    disabled: !localData.useDepartureActuarialRateRules,
                    required: localData.useDepartureActuarialRateRules,
                  }}
                  isRequired={!editMode}
                >
                  <Label text="Rules to apply" />
                </SimpleItem>
              </>
            )}

            {editMode && (
              <>
                {localData.actuarialRateRulesDeparture === 0 ? (
                  <SimpleItem
                    colSpan={4}
                    dataField="useDepartureActuarialRateRules"
                    editorType="dxCheckBox"
                    editorOptions={{
                      readOnly: true,
                      value: false,
                    }}
                  >
                    <Label text="Use Actuarial Rate" />
                  </SimpleItem>
                ) : (
                  <SimpleItem
                    colSpan={4}
                    dataField="actuarialRateRulesDeparture"
                    editorType="dxSelectBox"
                    editorOptions={{
                      dataSource: HORUS_ACTUARIAL_RATE_RULE,
                      displayExpr: "label",
                      valueExpr: "id",
                      readOnly: true,
                    }}
                  >
                    <Label text="Actuarial Rate Rule" />
                  </SimpleItem>
                )}
              </>
            )}
          </GroupItem>

          <GroupItem caption="Arrival" colCount={4}>
            <SimpleItem
              dataField="datasetArrivalId"
              colSpan={2}
              editorType="dxSelectBox"
              editorOptions={{
                dataSource: groupedDatasets,
                displayExpr: "description",
                valueExpr: "id",
                searchEnabled: true,
                grouped: true,
                readOnly: editMode,
                required: !editMode,
              }}
              isRequired={!editMode}
            >
              <Label text="Dataset" />
            </SimpleItem>

            <SimpleItem
              dataField="arrivalDatasetEvaluationDate"
              name="arrivalDatasetEvaluationDate"
              colSpan={2}
              editorType="dxDateBox"
              editorOptions={{
                readOnly: true,
                width: "100%",
                displayFormat: "yyyy-MM-dd",
                value: localData.arrivalDatasetEvaluationDate,
              }}
            >
              <Label text="Evaluation Date" />
            </SimpleItem>

            {!editMode && (
              <>
                <SimpleItem
                  colSpan={2}
                  dataField="useArrivalActuarialRateRules"
                  editorType="dxCheckBox"
                  editorOptions={{
                    readOnly: false,
                    onValueChanged: handleArrivalActuarialRateCheckboxChange,
                  }}
                >
                  <Label text="Use Actuarial Rate" />
                </SimpleItem>

                <SimpleItem
                  colSpan={2}
                  dataField="actuarialRateRulesArrival"
                  editorType="dxSelectBox"
                  editorOptions={{
                    dataSource: HORUS_ACTUARIAL_RATE_RULE,
                    displayExpr: "label",
                    valueExpr: "id",
                    readOnly: false,
                    disabled: !localData.useArrivalActuarialRateRules,
                    required: localData.useArrivalActuarialRateRules,
                  }}
                  isRequired={!editMode}
                >
                  <Label text="Rules to apply" />
                </SimpleItem>
              </>
            )}

            {editMode && (
              <>
                {localData.actuarialRateRulesArrival === 0 ? (
                  <SimpleItem
                    colSpan={4}
                    dataField="useArrivalActuarialRateRules"
                    editorType="dxCheckBox"
                    editorOptions={{
                      readOnly: true,
                      value: false,
                    }}
                  >
                    <Label text="Use Actuarial Rate" />
                  </SimpleItem>
                ) : (
                  <SimpleItem
                    colSpan={4}
                    dataField="actuarialRateRulesArrival"
                    editorType="dxSelectBox"
                    editorOptions={{
                      dataSource: HORUS_ACTUARIAL_RATE_RULE,
                      displayExpr: "label",
                      valueExpr: "id",
                      readOnly: true,
                    }}
                  >
                    <Label text="Actuarial Rate Rule" />
                  </SimpleItem>
                )}
              </>
            )}
          </GroupItem>
        </Form>
      </PopupWrapper>
    </>
  );
};

export default AddEditPopup;
